import React, { useState, useEffect } from 'react';
import {
  Settings,
  Users,
  AlertTriangle,
  CheckCircle,
  MessageSquare,
  X,
  Plus,
  Trash2,
  Clock,
  Mail,
  Smartphone,
  ChevronRight,
  ShieldAlert,
  Bot,
  Send,
  Check,
  XCircle,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  AlignLeft,
  Link2
} from 'lucide-react';


const LYWO_CSS = `
@charset "UTF-8";
@import url("https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css");
@import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");


:root {
  --color-blue-500: #3B7CF2;
  --color-indigo-100: #eef2ff;
  --color-green-100: #d1e7dd;
  --color-green-700: #198754;
  --color-yellow-100: #fff3cd;
  --color-yellow-700: #856404;
  --color-red-100: #f8d7da;
  --color-red-700: #dc3545;
  --color-gray-50: #F3F5F9;
  --color-gray-100: #e5e7eb;
  --color-gray-200: #d0d5dd;
  --color-gray-700: #667085;
  --color-gray-800: #344054;
  --color-gray-900: #1F222A;
 
  --brand-primary: var(--color-blue-500);
  --brand-bg: var(--color-gray-50);
  --brand-text: var(--color-gray-900);
  --surface-bg: #ffffff;
  --surface-bg-hover: var(--color-gray-50);
 
  --ui-gray-bg: var(--color-gray-100);
  --ui-border: var(--color-gray-200);
  --ui-text-muted: var(--color-gray-700);


  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;
 
  --type-ratio: 1.125;
  --font-size-base: 0.875rem;
  --font-size-sm: calc(var(--font-size-base) / var(--type-ratio));
  --font-size-h2: clamp(1.25rem, 1.1rem + 1vw, 1.5rem);
  --font-size-h3: clamp(1rem, 0.9rem + 0.5vw, 1.1875rem);
  --font-family-base: 'Inter', sans-serif;
 
  --line-height-heading: 1.35;
  --line-height-body: 1.5;    
  --margin-base: 1.5rem;
  --padding-card: 1.5rem;
}


body {
  margin: 0; padding: 0;
  font-family: var(--font-family-base) !important;
  background-color: var(--brand-bg);
  color: var(--brand-text);
  -webkit-font-smoothing: antialiased;
  line-height: var(--line-height-body);
  box-sizing: border-box;
  overflow-x: hidden;
}


*, *::before, *::after {
  box-sizing: inherit;
}


h2, .h2 { font-size: var(--font-size-h2) !important; line-height: var(--line-height-heading); font-weight: 700; letter-spacing: -0.01em; margin-bottom: calc(var(--margin-base) * 0.5); color: var(--brand-text); transition: margin 0.3s ease; }
h3, .h3 { font-size: var(--font-size-h3) !important; line-height: var(--line-height-heading); font-weight: 700; margin-bottom: calc(var(--margin-base) * 0.5); color: var(--brand-text); transition: margin 0.3s ease; }
.text-sm { font-size: var(--font-size-sm) !important; }
.text-muted { color: var(--ui-text-muted) !important; }


.ds-wrapper { background-color: var(--brand-bg); min-height: 100vh; display: flex; flex-direction: column; min-width: 320px; transition: background-color 0.3s; width: 100%; }
.ds-header { background: var(--surface-bg); border-bottom: 1px solid var(--ui-border); position: sticky; top: 0; z-index: 100; transition: background-color 0.3s; }
.ds-brand-row { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1.5rem; border-bottom: 1px solid var(--ui-gray-bg); }
.ds-nav-row { padding: 0 1.5rem; overflow-x: auto; white-space: nowrap; background: var(--surface-bg); }
.ds-content { flex: 1; padding: 1.5rem; width: 100%; max-width: 1200px; margin: 0 auto; transition: background-color 0.3s; }


.form-flex-col { display: flex; flex-direction: column; gap: var(--margin-base); transition: gap 0.3s ease; }


.btn { font-weight: 600; padding: 0.375rem 1rem; border-radius: var(--radius-md); display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; transition: 0.2s; font-size: var(--font-size-base) !important; cursor: pointer; border: 1px solid transparent; min-height: 38px; }
.btn-primary { background-color: var(--brand-primary); border-color: var(--brand-primary); color: #fff; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); }
.btn-primary:hover { filter: brightness(0.9); }
.btn-outline-primary { color: var(--brand-primary); border: 1px solid var(--brand-primary) !important; background: var(--surface-bg); }
.btn-outline-primary:hover { background-color: var(--color-indigo-100); }
.btn-ghost { background: none !important; border: none !important; cursor: pointer; padding: 0; line-height: 1; }


.custom-switch-wrap { display: flex; align-items: center; cursor: pointer; user-select: none; gap: 8px; min-height: 38px; }
.switch-input { position: absolute; opacity: 0; width: 0; height: 0; }
.switch-slider { position: relative; display: inline-block; width: 36px; height: 20px; background-color: var(--ui-border); border-radius: 34px; transition: background-color 0.3s; }
.switch-slider::before { position: absolute; content: ""; height: 16px; width: 16px; left: 2px; bottom: 2px; background-color: #fff; transition: transform 0.3s; border-radius: 50%; box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
.switch-input:checked + .switch-slider { background-color: var(--brand-primary); }
.switch-input:checked + .switch-slider::before { transform: translateX(16px); }


.form-control, .form-select { color: var(--brand-text) !important; font-size: var(--font-size-base) !important; border-radius: 8px !important; min-height: 38px !important; border: 1px solid var(--ui-border) !important; padding: 0.375rem 0.75rem !important; background: var(--surface-bg); width: 100%; transition: all 0.3s ease; }
.form-control:focus { outline: none; border-color: var(--brand-primary); box-shadow: 0 0 0 4px rgba(59, 124, 242, 0.15); }
.form-label { font-size: var(--font-size-base) !important; font-weight: 700 !important; color: var(--brand-text); margin-bottom: 6px; display: block; }


.card-std { background: var(--surface-bg); border: 1px solid var(--ui-border); border-radius: var(--radius-lg); padding: var(--padding-card); display: flex; flex-direction: column; margin-bottom: var(--margin-base); box-shadow: 0 1px 3px rgba(0,0,0,0.05); }


.badge { display: inline-flex; align-items: center; padding: 0.25em 0.6em; border-radius: 9999px; font-size: var(--font-size-sm); font-weight: 600; line-height: 1; border: 1px solid transparent; white-space: nowrap; }
.badge-excellent { background-color: var(--color-green-100); color: var(--color-green-700); }
.badge-normal { background-color: var(--color-yellow-100); color: var(--color-yellow-700); }
.badge-unsure { background-color: var(--ui-gray-bg); color: var(--ui-text-muted); }


.nav-tabs-horizontal { display: flex; gap: 2rem; border-bottom: 1px solid transparent; margin: 0; padding: 0; list-style: none; }
.nav-item-h { padding: 0.75rem 0.25rem; cursor: pointer; color: var(--ui-text-muted); font-weight: 500; font-size: var(--font-size-base); border-bottom: 2px solid transparent; transition: all 0.2s; display: flex; align-items: center; gap: 0.5rem; min-height: 38px; }
.nav-item-h.active { color: var(--brand-primary); border-bottom-color: var(--brand-primary); font-weight: 600; }


.custom-modal-overlay { position: fixed; inset: 0; background-color: rgba(0,0,0,0.5); backdrop-filter: blur(4px); z-index: 1100; display: flex; align-items: center; justify-content: center; animation: fadeIn 0.2s ease-out; }
.custom-modal-overlay.side-panel-overlay { justify-content: flex-end; }
.custom-modal-dialog { background: var(--surface-bg); width: 100%; max-width: 500px; max-height: 90vh; border-radius: var(--radius-lg); box-shadow: 0 10px 25px rgba(0,0,0,0.2); display: flex; flex-direction: column; overflow: hidden; animation: slideUp 0.3s ease-out; }
.custom-modal-header { padding: 1rem 1.25rem; border-bottom: 1px solid var(--ui-border); display: flex; justify-content: space-between; align-items: center; background: var(--surface-bg); }
.custom-modal-body { padding: 1.25rem; flex: 1; overflow-y: auto; background: var(--surface-bg); }
.custom-modal-footer { padding: 1rem 1.25rem; border-top: 1px solid var(--ui-border); background: var(--surface-bg); display: flex; justify-content: flex-end; gap: 0.75rem; }


.side-panel { background: var(--surface-bg); width: 100%; max-width: 500px; height: 100vh; box-shadow: -10px 0 25px rgba(0,0,0,0.1); display: flex; flex-direction: column; animation: slideInRight 0.3s ease-out; margin-left: auto; }


.custom-checkbox { width: 16px; height: 16px; border-radius: 4px; border: 1px solid var(--ui-border); appearance: none; -webkit-appearance: none; outline: none; cursor: pointer; position: relative; background-color: var(--surface-bg); transition: all 0.2s; flex-shrink: 0; margin: 0; }
.custom-checkbox:checked { border-color: var(--brand-primary); background-color: var(--brand-primary); }
.custom-checkbox:checked::after { content: "✓"; font-size: 10px; font-weight: bold; color: white; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); }


@keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;


const MOCK_CANDIDATES = [
  {
    id: "C-1001",
    name: "Alexia Vance",
    appliedDate: "Oct 24, 2025",
    tokenExpiry: "Oct 31, 2025",
    status: "Screening Complete",
    recommendation: "Recommended",
    fraudFlag: false,
    transcript: [
      { sender: 'AI', text: "Hi Alexia! I'm LYWO's AI assistant. Ready for a quick chat about the Senior Frontend role?" },
      { sender: 'Candidate', text: "Yes, absolutely! Happy to chat." },
      { sender: 'AI', text: "Great. First, are you still interested in this position and comfortable with a hybrid work setup in New York?" },
      { sender: 'Candidate', text: "Yes, I am currently based in Brooklyn so hybrid in NY is perfect." },
      { sender: 'AI', text: "Let's dive into a quick technical question. How do you handle state management in a large-scale React application?" },
      { sender: 'Candidate', text: "I typically use Redux Toolkit for global state, but I lean heavily on React Query for server state caching. It keeps the UI snappy and reduces boilerplate." }
    ]
  },
  {
    id: "C-1002",
    name: "Marcus Thorne",
    appliedDate: "Oct 25, 2025",
    tokenExpiry: "Nov 01, 2025",
    status: "Screening Complete",
    recommendation: "Maybe Not",
    fraudFlag: true,
    fraudReason: "2nd Rejoin Rule Triggered (Test environment compromised)",
    transcript: [
      { sender: 'AI', text: "Hi Marcus! Ready for your screening for the Backend Developer role?" },
      { sender: 'Candidate', text: "Yep, let's go." },
      { sender: 'AI', text: "Can you explain how you would optimize a slow-performing SQL query?" },
      { sender: 'Candidate', text: "Let me check." },
      { sender: 'System', text: "⚠️ [SYSTEM ALERT: Candidate disconnected and rejoined 2 times within 3 minutes]" },
      { sender: 'Candidate', text: "I would use EXPLAIN to look at the query plan and add indexes to the columns being filtered." }
    ]
  },
  {
    id: "C-1003",
    name: "Elena Rodriguez",
    appliedDate: "Oct 26, 2025",
    tokenExpiry: "Nov 02, 2025",
    status: "Pending Response",
    recommendation: "Unsure",
    fraudFlag: false,
    transcript: [
      { sender: 'AI', text: "Hi Elena! Are you available to start the screening process?" },
      { sender: 'Candidate', text: "I am currently traveling, can we do this tomorrow?" },
      { sender: 'AI', text: "Of course! The link will remain active for 7 days. Just click it whenever you're ready." }
    ]
  }
];


// ============================================================================
// COMPONENT: Review Automated Email Modal
// ============================================================================
const EmailModal = ({ config, onClose }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [cc, setCc] = useState('');
 
  useEffect(() => {
    if (!config.isOpen || config.candidates.length === 0) return;


    setSubject('Update regarding your application for the position of Sales Executive');


    if (config.type === 'advance') {
      setMessage(`Thank you for completing the chat screening for the Sales Executive role at LYWO.\n\nWe appreciate your detailed responses and are pleased to include your application in the active candidate pool for this position.\n\nWe look forward to learning more about your skills and experience as we move forward. Please allow us time to continue with the application process.\n\nBest Regards,\nLYWO Recruitment Team\nPowered by LYWO`);
    } else if (config.type === 'decline') {
      setMessage(`Thank you for completing the chat screening for the Sales Executive role at LYWO.\n\nWhile we were impressed with your background, we have decided to move forward with other candidates whose experience more closely aligns with our current requirements.\n\nWe wish you the best in your job search and future professional endeavors.\n\nBest Regards,\nLYWO Recruitment Team\nPowered by LYWO`);
    } else {
      setSubject('Test Message: LYWO Screening System');
      setMessage(`This is a manual test message triggered from the LYWO screening dashboard.\n\nPlease confirm receipt so we can ensure our communication channels are working correctly.\n\nBest,\nLYWO Recruitment Team\nPowered by LYWO`);
    }
  }, [config]);


  if (!config.isOpen) return null;


  return (
    <div className="custom-modal-overlay" style={{ zIndex: 1200 }}>
      {/* Expanding max-width to 800px to match the wider email composition screen */}
      <div className="custom-modal-dialog" style={{ maxWidth: '800px', width: '90%' }}>
       
        {/* Minimal Header with just the close button */}
        <div className="flex justify-end p-3 border-b border-[var(--ui-border)] bg-[var(--surface-bg)]">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 bg-transparent border-none cursor-pointer p-1 rounded hover:bg-gray-100 transition-colors">
            <X size={20}/>
          </button>
        </div>


        <div className="custom-modal-body flex flex-col gap-5 p-6">
         
          {/* Metadata Grid */}
          <div className="grid grid-cols-[80px_1fr] gap-y-4 gap-x-4 items-center">
            <div className="text-sm font-bold text-gray-800">To</div>
            <div className="text-sm text-gray-700">
              {config.candidates.length} / {config.candidates.length} Selected
            </div>


            <div className="text-sm font-bold text-gray-800">Excl</div>
            <div className="text-sm text-gray-700">
              0 Not Interested, 0 Active / Completed
            </div>


            <div className="text-sm font-bold text-gray-800">Subject</div>
            <div>
              <input
                type="text"
                className="form-control text-sm"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>


            <div className="text-sm font-bold text-gray-800">From</div>
            <div className="text-sm text-gray-700">
              hello@lywo.in
            </div>


            <div className="text-sm font-bold text-gray-800">Cc</div>
            <div>
              <input
                type="text"
                className="form-control text-sm"
                placeholder="Add email and press enter"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
              />
            </div>
          </div>


          {/* Rich Text Editor Mockup */}
          <div className="border border-[var(--ui-border)] rounded-md flex flex-col mt-2">
            {/* Toolbar */}
            <div className="border-b border-[var(--ui-border)] p-2 flex gap-4 items-center bg-white">
              <div className="flex gap-2 text-gray-600">
                <button className="p-1.5 hover:bg-gray-100 rounded cursor-pointer border-none bg-transparent"><Bold size={16} /></button>
                <button className="p-1.5 hover:bg-gray-100 rounded cursor-pointer border-none bg-transparent"><Italic size={16} /></button>
                <button className="p-1.5 hover:bg-gray-100 rounded cursor-pointer border-none bg-transparent"><Underline size={16} /></button>
              </div>
              <div className="w-px h-5 bg-gray-300"></div>
              <div className="flex gap-2 text-gray-600">
                <button className="p-1.5 hover:bg-gray-100 rounded cursor-pointer border-none bg-transparent"><List size={16} /></button>
                <button className="p-1.5 hover:bg-gray-100 rounded cursor-pointer border-none bg-transparent"><ListOrdered size={16} /></button>
                <button className="p-1.5 hover:bg-gray-100 rounded cursor-pointer border-none bg-transparent"><AlignLeft size={16} /></button>
              </div>
              <div className="w-px h-5 bg-gray-300"></div>
              <div className="flex gap-2 text-gray-600">
                <button className="p-1.5 hover:bg-gray-100 rounded cursor-pointer border-none bg-transparent"><Link2 size={16} /></button>
              </div>
            </div>
            {/* Text Area */}
            <textarea
              className="w-full min-h-[220px] p-4 text-sm text-gray-800 border-none outline-none resize-y bg-white"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              style={{ lineHeight: '1.6' }}
            />
          </div>


        </div>


        {/* Footer */}
        <div className="p-4 border-t border-[var(--ui-border)] bg-white flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">Use last sent template</span>
            <label className="custom-switch-wrap m-0 min-h-0">
              <input type="checkbox" className="switch-input" />
              <span className="switch-slider"></span>
            </label>
            <span className="text-sm text-gray-600 ml-2">Fetch default template</span>
          </div>
         
          <div className="flex gap-3">
            <button className="btn btn-outline-primary" style={{ borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }}>
              Test Email
            </button>
            <button className="btn btn-primary px-6" onClick={onClose} style={{ backgroundColor: '#0033cc', borderColor: '#0033cc' }}>
              Send
            </button>
          </div>
        </div>


      </div>
    </div>
  );
};


// ============================================================================
// COMPONENT 1: Recruiter Configuration Screen
// ============================================================================
const AIChatConfigForm = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [config, setConfig] = useState({
    expiryDays: 7,
    channels: ['email', 'whatsapp'],
    activities: {
      interest: true,
      requirements: true,
      technical: true,
      behavior: false,
      logistics: true,
      referrals: false
    },
    timerMinutes: 3,
    questions: [
      { id: 1, text: "Describe a time you had to optimize a React component's rendering performance.", isClean: true, timerMinutes: 3 },
      { id: 2, text: "How old are you, and do you have family commitments that would prevent weekend work?", isClean: false, timerMinutes: 3 }
    ]
  });


  const handleToggle = (key) => {
    setConfig(prev => ({
      ...prev,
      activities: { ...prev.activities, [key]: !prev.activities[key] }
    }));
  };


  const handleChannelToggle = (channel) => {
    setConfig(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel]
    }));
  };


  const addQuestion = () => {
    setConfig(prev => ({
      ...prev,
      questions: [...prev.questions, { id: Date.now(), text: "", isClean: true, timerMinutes: prev.timerMinutes }]
    }));
  };
  return (
    <div className="card-std animate-in fade-in duration-300">
      <div className="mb-4">
        <h2 className="h2 mb-1">AI Chat Screening Configuration</h2>
        <p className="text-muted text-sm m-0">Configure parameters and guardrails for the AI screening agent.</p>
      </div>


      <ul className="nav-tabs-horizontal mb-4 border-b border-[var(--ui-border)]">
        <li className={`nav-item-h ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>
          <Settings size={16} /> General Settings
        </li>
        <li className={`nav-item-h ${activeTab === 'activities' ? 'active' : ''}`} onClick={() => setActiveTab('activities')}>
          <Users size={16} /> Activity Toggles
        </li>
        <li className={`nav-item-h ${activeTab === 'technical' ? 'active' : ''}`} onClick={() => setActiveTab('technical')}>
          <MessageSquare size={16} /> Technical Assessment
        </li>
      </ul>


      <div className="mt-4">
        {/* TAB: GENERAL SETTINGS */}
        {activeTab === 'general' && (
          <div className="form-flex-col max-w-2xl gap-4">
            <div>
              <label className="form-label">Token Expiry (Days)</label>
              <input
                type="number"
                className="form-control"
                value={config.expiryDays}
                onChange={(e) => setConfig({...config, expiryDays: e.target.value})}
                min="1" max="30"
              />
              <p className="text-xs text-gray-500 mt-1">Number of days the candidate has to initiate the chat before the link expires.</p>
            </div>
           
            <div>
              <label className="form-label mb-2">Delivery Channels</label>
              <div className="flex gap-3 flex-wrap">
                <label className="flex items-center gap-2 p-2.5 border rounded-lg bg-gray-50 cursor-pointer hover:border-blue-500 transition-colors">
                  <input type="checkbox" checked={config.channels.includes('email')} onChange={() => handleChannelToggle('email')} className="w-4 h-4 text-blue-600" />
                  <Mail size={16} className="text-gray-600" /> Email
                </label>
                <label className="flex items-center gap-2 p-2.5 border rounded-lg bg-gray-50 cursor-pointer hover:border-blue-500 transition-colors">
                  <input type="checkbox" checked={config.channels.includes('whatsapp')} onChange={() => handleChannelToggle('whatsapp')} className="w-4 h-4 text-blue-600" />
                  <MessageSquare size={16} className="text-green-600" /> WhatsApp
                </label>
                <label className="flex items-center gap-2 p-2.5 border rounded-lg bg-gray-50 cursor-pointer hover:border-blue-500 transition-colors">
                  <input type="checkbox" checked={config.channels.includes('sms')} onChange={() => handleChannelToggle('sms')} className="w-4 h-4 text-blue-600" />
                  <Smartphone size={16} className="text-gray-600" /> SMS
                </label>
              </div>
            </div>
          </div>
        )}


        {/* TAB: ACTIVITY TOGGLES */}
        {activeTab === 'activities' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
            {[
              { key: 'interest', title: "1. Continued Interest", desc: "Verify if the candidate is still interested in the role." },
              { key: 'requirements', title: "2. Verify Key Requirements", desc: "Check non-negotiables (location, salary, visa status)." },
              { key: 'technical', title: "3. Technical Topic", desc: "Ask specific technical questions from the bank." },
              { key: 'behavior', title: "4. Behaviour Check", desc: "Conduct a brief situational judgment test." },
              { key: 'logistics', title: "5. Next-Step Logistics", desc: "Collect availability for the next interview round." },
              { key: 'referrals', title: "6. Optional Referrals", desc: "Ask if they know anyone else suitable for LYWO." }
            ].map(act => (
              <div key={act.key} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                <label className="custom-switch-wrap mt-1">
                  <input type="checkbox" className="switch-input" checked={config.activities[act.key]} onChange={() => handleToggle(act.key)} />
                  <span className="switch-slider"></span>
                </label>
                <div>
                  <h4 className="text-sm font-bold m-0">{act.title}</h4>
                  <p className="text-xs text-gray-500 mt-1">{act.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}


        {/* TAB: TECHNICAL ASSESSMENT */}
        {activeTab === 'technical' && (
          <div className="flex flex-col gap-3 max-w-3xl">
           
            {/* Ultra-compact Inline Timer row */}
             <div className="flex items-center justify-between gap-3 bg-[var(--color-indigo-100)] p-2 rounded-md border border-[var(--color-blue-200)] mb-2 shadow-sm">
              <div className="flex items-center gap-2">
                <Clock size={14} className="text-blue-600" />
                <span className="text-xs font-semibold text-blue-900 whitespace-nowrap">Default Timer (Mins):</span>
                <input
                  type="number"
                  className="form-control w-14 min-h-0 h-7 text-xs py-0 px-2 text-center border-blue-300"
                  value={config.timerMinutes}
                  onChange={(e) => setConfig({...config, timerMinutes: e.target.value})}
                  min="1" max="15"
                />
              </div>
              <button
                className="btn btn-outline-primary text-[10px] py-1 px-3 min-h-0 h-7 bg-white whitespace-nowrap border-blue-300"
                onClick={() => {
                  const updatedQ = config.questions.map(q => ({...q, timerMinutes: config.timerMinutes}));
                  setConfig({...config, questions: updatedQ});
                }}
                title="Apply this default timer to all existing questions below"
              >
                Apply to All
              </button>
            </div>


            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="form-label m-0 text-sm">Custom Question Bank</label>
                <button onClick={addQuestion} className="btn btn-ghost text-[var(--brand-primary)] text-xs py-1 px-2 min-h-0 h-auto hover:bg-blue-50">
                  <Plus size={14} /> Add Question
                </button>
              </div>
             
              <div className="flex flex-col gap-1.5">
                {config.questions.map((q, index) => (
                  <div key={q.id} className="group flex items-center gap-2 border border-[var(--ui-border)] rounded-md p-1.5 bg-white shadow-sm hover:border-[var(--brand-primary)] focus-within:border-[var(--brand-primary)] focus-within:ring-1 focus-within:ring-[var(--color-indigo-100)] transition-all">
                   
                    <div className="flex-shrink-0 w-6 text-center font-bold text-gray-400 text-[10px]">
                      {index + 1}.
                    </div>


                    <input
                      type="text"
                      className="flex-1 border-none outline-none text-xs bg-transparent py-1 px-0 text-gray-800 m-0 w-full"
                      placeholder="Type your technical or behavioral question here..."
                      value={q.text}
                      onChange={(e) => {
                        const newQ = [...config.questions];
                        newQ[index].text = e.target.value;
                        newQ[index].isClean = !(/age|family/i.test(e.target.value));
                        setConfig({...config, questions: newQ});
                      }}
                    />


                    {/* Compact Linting Status Icon */}
                    {q.text.length > 0 && (
                      <div className="flex-shrink-0 flex justify-center w-5" title={q.isClean ? "Passed AI Linting" : "Flagged: Protected Characteristics"}>
                        {q.isClean ? <CheckCircle size={14} className="text-green-500"/> : <AlertTriangle size={14} className="text-red-500"/>}
                      </div>
                    )}


                    <div className="w-px h-4 bg-gray-200 mx-0.5 hidden sm:block"></div>


                    {/* Compact Individual Question Timer */}
                    <div className="flex items-center gap-1 flex-shrink-0 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 hover:border-gray-300 transition-colors" title="Time limit for this specific question">
                      <Clock size={10} className="text-gray-400" />
                      <input
                        type="number"
                        className="w-6 border-none outline-none text-[11px] text-center bg-transparent p-0 m-0 text-gray-700 font-medium"
                        value={q.timerMinutes || ''}
                        onChange={(e) => {
                          const newQ = [...config.questions];
                          newQ[index].timerMinutes = e.target.value;
                          setConfig({...config, questions: newQ});
                        }}
                        min="1"
                        style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }}
                      />
                      <span className="text-[10px] text-gray-500 font-medium">m</span>
                    </div>


                    {/* Compact Delete Action */}
                    <button
                      onClick={() => setConfig({...config, questions: config.questions.filter(item => item.id !== q.id)})}
                      className="text-gray-300 hover:text-red-500 transition-colors p-1 flex-shrink-0 ml-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                      title="Remove Question"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {config.questions.length === 0 && (
                   <div className="text-center py-4 border border-dashed rounded-lg text-gray-400 text-xs bg-gray-50">
                     No custom questions added. AI will use standard defaults.
                   </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
     
      <div className="mt-5 pt-3 border-t flex justify-end gap-3">
        <button className="btn btn-ghost text-muted">Cancel</button>
        <button className="btn btn-primary">Save Configuration</button>
      </div>
    </div>
  );
};


// ============================================================================
// COMPONENT 2: Review & Decision Dashboard
// ============================================================================
const AIChatCandidateList = ({ onReviewCandidate, onActionClick }) => {
  const [selectedIds, setSelectedIds] = useState([]);


  const toggleSelectAll = () => {
    if (selectedIds.length === MOCK_CANDIDATES.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(MOCK_CANDIDATES.map(c => c.id));
    }
  };


  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };


  const getBadgeClass = (rec) => {
    switch(rec) {
      case 'Recommended': return 'badge-excellent';
      case 'Maybe Not': return 'badge-normal';
      case 'Unsure': return 'badge-unsure';
      default: return 'badge-unsure';
    }
  };


  return (
    <div className="card-std animate-in fade-in duration-300">
      <div className="mb-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="h2 mb-1">Review & Decision Dashboard</h2>
          <p className="text-muted text-sm m-0">Review AI screening results and take action on candidates.</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <input type="text" placeholder="Search candidates..." className="form-control w-full sm:w-64" />
        </div>
      </div>


      <div className="overflow-x-auto w-full">
        {selectedIds.length > 0 && (
          <div className="bg-[var(--color-indigo-100)] border border-[var(--brand-primary)] rounded-lg p-2 mb-3 flex justify-between items-center animate-in fade-in slide-in-from-top-2">
             <span className="text-sm font-semibold text-[var(--brand-primary)] ml-2">{selectedIds.length} candidate(s) selected</span>
             <div className="flex gap-2">
                <button
                  className="btn btn-outline-primary bg-white text-xs py-1 px-3 min-h-0 h-auto"
                  onClick={() => {
                    const selectedCands = MOCK_CANDIDATES.filter(c => selectedIds.includes(c.id));
                    onActionClick('test', selectedCands);
                  }}
                >
                   <Send size={14} /> Test Message
                </button>
             </div>
          </div>
        )}


        <table className="w-full text-left border-collapse whitespace-nowrap">
          <thead>
            <tr className="border-b bg-[var(--ui-gray-bg)] text-[var(--ui-text-dark)] text-sm">
              <th className="py-2.5 px-3 rounded-tl-lg w-10">
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={selectedIds.length === MOCK_CANDIDATES.length && MOCK_CANDIDATES.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="py-2.5 px-3 font-semibold">Candidate Name</th>
              <th className="py-2.5 px-3 font-semibold">Applied Date</th>
              <th className="py-2.5 px-3 font-semibold">Token Expiry</th>
              <th className="py-2.5 px-3 font-semibold">Status</th>
              <th className="py-2.5 px-3 font-semibold">AI Recommendation</th>
              <th className="py-2.5 px-3 font-semibold">Alerts</th>
              <th className="py-2.5 px-3 font-semibold rounded-tr-lg">Actions</th>
            </tr>
          </thead>
          <tbody className="text-sm divide-y divide-[var(--ui-border)]">
            {MOCK_CANDIDATES.map((cand) => (
              <tr key={cand.id} className="hover:bg-[var(--surface-bg-hover)] transition-colors group">
                <td className="py-2.5 px-3">
                  <input
                    type="checkbox"
                    className="custom-checkbox"
                    checked={selectedIds.includes(cand.id)}
                    onChange={() => toggleSelect(cand.id)}
                  />
                </td>
                <td className="py-2.5 px-3 font-medium text-[var(--brand-text)]">{cand.name}</td>
                <td className="py-2.5 px-3 text-[var(--ui-text-muted)]">{cand.appliedDate}</td>
                <td className="py-2.5 px-3 text-[var(--ui-text-muted)]">{cand.tokenExpiry}</td>
                <td className="py-2.5 px-3 text-[var(--ui-text-muted)]">{cand.status}</td>
                <td className="py-2.5 px-3">
                  <span className={`badge ${getBadgeClass(cand.recommendation)}`}>
                    {cand.recommendation}
                  </span>
                </td>
                <td className="py-2.5 px-3">
                  {cand.fraudFlag ? (
                    <div className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-0.5 rounded w-max border border-red-100" title={cand.fraudReason}>
                      <ShieldAlert size={12} />
                      <span className="text-[10px] font-semibold">Fraud Flag</span>
                    </div>
                  ) : (
                    <span className="text-gray-300">-</span>
                  )}
                </td>
                <td className="py-2.5 px-3">
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => onReviewCandidate(cand)}
                      className="btn btn-outline-primary text-xs py-1 px-3 min-h-0 h-8"
                    >
                      Review
                    </button>
                    <button
                      onClick={() => onActionClick('advance', [cand])}
                      className="btn btn-ghost text-green-600 hover:bg-green-50 p-1 min-h-0 h-8 w-8 border border-transparent hover:border-green-200"
                      title="Advance Candidate"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={() => onActionClick('decline', [cand])}
                      className="btn btn-ghost text-red-600 hover:bg-red-50 p-1 min-h-0 h-8 w-8 border border-transparent hover:border-red-200"
                      title="Respectful Decline"
                    >
                      <XCircle size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ============================================================================
// COMPONENT 3: Candidate Transcript Side Panel
// ============================================================================
const TranscriptSidePanel = ({ candidate, onClose, onActionClick }) => {
  if (!candidate) return null;


  const getBadgeClass = (rec) => {
    switch(rec) {
      case 'Recommended': return 'badge-excellent';
      case 'Maybe Not': return 'badge-normal';
      case 'Unsure': return 'badge-unsure';
      default: return 'badge-unsure';
    }
  };


  return (
    <div className="custom-modal-overlay side-panel-overlay">
      <div className="side-panel">
        {/* HEADER */}
        <div className="p-5 border-b flex justify-between items-start bg-white">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="h2 m-0">{candidate.name}</h2>
              <span className={`badge ${getBadgeClass(candidate.recommendation)}`}>
                AI: {candidate.recommendation}
              </span>
            </div>
            <p className="text-sm text-muted m-0">Applied: {candidate.appliedDate} • ID: {candidate.id}</p>
           
            {candidate.fraudFlag && (
              <div className="mt-2 flex items-center gap-2 p-2 bg-red-50 border border-red-200 text-red-700 rounded text-sm font-medium">
                <AlertTriangle size={16} />
                {candidate.fraudReason}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors border-none bg-transparent cursor-pointer">
            <X size={20} />
          </button>
        </div>


        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-5 bg-gray-50/50">
         
          {/* Summary Section */}
          <div className="mb-6">
            <h3 className="text-xs font-bold text-gray-900 mb-3 uppercase tracking-wider">Screening Summary</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Interest", status: "Verified" },
                { label: "Requirements", status: "Met (Hybrid NY)" },
                { label: "Technical", status: "Completed" },
                { label: "Behaviour", status: "Skipped" },
                { label: "Logistics", status: "Available Mon-Wed" },
                { label: "Referrals", status: "None" }
              ].map((item, idx) => (
                <div key={idx} className="bg-white p-2.5 rounded border shadow-sm flex flex-col gap-0.5">
                  <span className="text-[10px] text-gray-500 font-semibold uppercase">{item.label}</span>
                  <span className="text-sm text-gray-900 font-medium">{item.status}</span>
                </div>
              ))}
            </div>
          </div>


          {/* Transcript Section */}
          <div>
            <h3 className="text-xs font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <MessageSquare size={14} /> Raw Chat Transcript
            </h3>
            <div className="space-y-3">
              {candidate.transcript.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.sender === 'Candidate' ? 'justify-end' : 'justify-start'}`}>
                  {msg.sender === 'System' ? (
                     <div className="w-full text-center my-1 text-xs font-bold text-red-600 bg-red-50 py-1 rounded border border-red-100">
                       {msg.text}
                     </div>
                  ) : (
                    <div className={`max-w-[80%] rounded-xl p-2.5 text-sm ${
                      msg.sender === 'Candidate'
                        ? 'bg-[var(--brand-primary)] text-white rounded-tr-sm'
                        : 'bg-white border text-gray-800 rounded-tl-sm shadow-sm'
                    }`}>
                      {msg.sender === 'AI' && (
                        <div className="text-[10px] font-bold text-blue-500 mb-1 flex items-center gap-1 uppercase">
                          <Bot size={12}/> LYWO AI
                        </div>
                      )}
                      {msg.text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>


        </div>


        {/* FOOTER */}
        <div className="p-3 border-t bg-white flex justify-end gap-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <button
            className="btn btn-ghost text-red-600 px-4 hover:bg-red-50"
            onClick={() => onActionClick('decline', [candidate])}
          >
            Respectful Decline
          </button>
          <button
            className="btn btn-primary"
            onClick={() => onActionClick('advance', [candidate])}
          >
            Advance Candidate <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// MAIN APP COMPONENT (Container)
// ============================================================================
export default function Screening() {
  const [view, setView] = useState('dashboard');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [emailModalConfig, setEmailModalConfig] = useState({ isOpen: false, type: 'test', candidates: [] });


  const handleActionClick = (type, candidates) => {
    setEmailModalConfig({ isOpen: true, type, candidates });
  };


  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = LYWO_CSS;
    document.head.appendChild(styleSheet);
    return () => { document.head.removeChild(styleSheet); };
  }, []);


  return (
    <div className="ds-wrapper font-sans">
     
      {/* LYWO GLOBAL HEADER */}
      <header className="ds-header">
        <div className="ds-brand-row">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-blue-600 text-white rounded flex items-center justify-center font-bold text-base">L</div>
            <h1 className="h3 m-0">LYWO AI Chat Screening</h1>
          </div>
         
          <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg text-sm">
             <span className="font-semibold text-gray-900">Sales Executive</span>
             <span className="text-gray-300">|</span>
             <span className="text-xs font-bold text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">CODE: TSTLYWO</span>
          </div>
        </div>
       
        {/* TABBED NAVIGATION ROW */}
        <div className="ds-nav-row">
          <ul className="nav-tabs-horizontal">
            <li
              className={`nav-item-h ${view === 'config' ? 'active' : ''}`}
              onClick={() => setView('config')}
            >
              AI Configuration
            </li>
            <li
              className={`nav-item-h ${view === 'dashboard' ? 'active' : ''}`}
              onClick={() => setView('dashboard')}
            >
              Review Dashboard
            </li>
          </ul>
        </div>
      </header>


      {/* MAIN CONTENT AREA */}
      <main className="ds-content">
        {view === 'config' ? (
          <AIChatConfigForm />
        ) : (
          <AIChatCandidateList
            onReviewCandidate={setSelectedCandidate}
            onActionClick={handleActionClick}
          />
        )}
      </main>


      {/* PORTALS & OVERLAYS */}
      {selectedCandidate && (
        <TranscriptSidePanel
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
          onActionClick={handleActionClick}
        />
      )}
     
      <EmailModal
        config={emailModalConfig}
        onClose={() => setEmailModalConfig({ ...emailModalConfig, isOpen: false })}
      />
    </div>
  );
}

